import { useMemo, useState } from "react";
import { formatDistanceToNow, parseISO } from "date-fns";
import { useAppStore } from "../store/useAppStore";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  BellRing,
  CheckCircle2,
  Copy,
  ExternalLink,
  Mail,
  MessageSquareText,
  Archive,
  Sparkles,
  Trash2,
  WandSparkles,
} from "lucide-react";
import type { HiddenSignal, OutreachDraft } from "../types";

const statusTone: Record<string, string> = {
  new: "bg-amber-100 text-amber-800 border-amber-200",
  reviewed: "bg-slate-100 text-slate-700 border-slate-200",
  promoted: "bg-emerald-100 text-emerald-800 border-emerald-200",
  dismissed: "bg-rose-100 text-rose-800 border-rose-200",
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  queued: "bg-blue-100 text-blue-800 border-blue-200",
  sent: "bg-emerald-100 text-emerald-800 border-emerald-200",
  archived: "bg-zinc-100 text-zinc-700 border-zinc-200",
};

const severityTone: Record<string, string> = {
  low: "bg-slate-100 text-slate-700 border-slate-200",
  medium: "bg-amber-100 text-amber-800 border-amber-200",
  high: "bg-orange-100 text-orange-800 border-orange-200",
  critical: "bg-red-100 text-red-800 border-red-200",
};

function channelLabel(channel: OutreachDraft["channel"]) {
  switch (channel) {
    case "email":
      return "Email";
    case "call":
      return "Call";
    case "letter":
      return "Letter";
    default:
      return "SMS";
  }
}

function statusLabel(status: string) {
  return status.replace(/_/g, " ");
}

function formatRelative(iso: string) {
  try {
    return `${formatDistanceToNow(parseISO(iso), { addSuffix: true })}`;
  } catch {
    return iso;
  }
}

export default function SignalsAndOutreach() {
  const {
    hiddenSignals,
    outreachDrafts,
    promoteSignalToOpportunity,
    createOutreachDraft,
    updateOutreachDraft,
    deleteOutreachDraft,
  } = useAppStore();
  const [tab, setTab] = useState("signals");
  const [workingId, setWorkingId] = useState<string | null>(null);

  const signals = useMemo(
    () => [...hiddenSignals].sort((a, b) => parseISO(b.detectedAt).getTime() - parseISO(a.detectedAt).getTime()),
    [hiddenSignals],
  );
  const drafts = useMemo(
    () => [...outreachDrafts].sort((a, b) => parseISO(b.updatedAt).getTime() - parseISO(a.updatedAt).getTime()),
    [outreachDrafts],
  );

  const createDraftFromSignal = async (signal: HiddenSignal, channel: OutreachDraft["channel"]) => {
    setWorkingId(signal.id);
    try {
      const subject = channel === "email"
        ? `Quick note about ${signal.propertyAddress}`
        : `Reach out about ${signal.propertyAddress}`;

      const message = [
        `Hi, I noticed ${signal.propertyAddress} and wanted to reach out.`,
        signal.title ? `I saw a signal for ${signal.title.toLowerCase()}.` : null,
        signal.description ? signal.description : null,
        "If this is relevant, I’d be happy to share a few options that could help.",
      ]
        .filter(Boolean)
        .join(" ");

      await createOutreachDraft({
        originType: "signal",
        signal,
        channel,
        subject,
        message,
      });
      toast.success(`${channelLabel(channel)} draft saved`);
    } catch (error) {
      console.error(error);
      toast.error("Failed to create outreach draft");
    } finally {
      setWorkingId(null);
    }
  };

  const handlePromote = async (signal: HiddenSignal) => {
    setWorkingId(signal.id);
    try {
      await promoteSignalToOpportunity(signal.id);
      toast.success("Promoted to opportunity");
    } catch (error) {
      console.error(error);
      toast.error("Failed to promote signal");
    } finally {
      setWorkingId(null);
    }
  };

  const handleCopyDraft = async (draft: OutreachDraft) => {
    try {
      await navigator.clipboard.writeText(`${draft.subject}\n\n${draft.message}`);
      toast.success("Draft copied to clipboard");
    } catch {
      toast.error("Could not copy draft");
    }
  };

  const handleMarkSent = async (draft: OutreachDraft) => {
    setWorkingId(draft.id);
    try {
      await updateOutreachDraft(draft.id, { status: "sent", sentAt: new Date().toISOString() });
      toast.success("Marked as sent");
    } catch (error) {
      console.error(error);
      toast.error("Failed to update draft");
    } finally {
      setWorkingId(null);
    }
  };

  const handleArchive = async (draft: OutreachDraft) => {
    setWorkingId(draft.id);
    try {
      await updateOutreachDraft(draft.id, { status: "archived" });
      toast.success("Archived draft");
    } catch (error) {
      console.error(error);
      toast.error("Failed to archive draft");
    } finally {
      setWorkingId(null);
    }
  };

  const handleDelete = async (draft: OutreachDraft) => {
    setWorkingId(draft.id);
    try {
      await deleteOutreachDraft(draft.id);
      toast.success("Deleted draft");
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete draft");
    } finally {
      setWorkingId(null);
    }
  };

  return (
    <div className="flex flex-col gap-6 w-full max-w-6xl mx-auto">
      <div className="flex bg-white border-b border-slate-200 items-center justify-between px-8 py-4 -mx-4 sm:-mx-8 -mt-4 sm:-mt-8 mb-2 flex-shrink-0">
        <div>
          <h1 className="text-xl font-bold">Signals & Outreach</h1>
          <p className="text-sm text-slate-500">Track hidden deal signals and persist outreach drafts in one place.</p>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center">
              <BellRing className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Signals</p>
              <p className="text-2xl font-semibold text-slate-900">{signals.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center">
              <MessageSquareText className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Drafts</p>
              <p className="text-2xl font-semibold text-slate-900">{drafts.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Ready to act</p>
              <p className="text-2xl font-semibold text-slate-900">{signals.filter((signal) => signal.status === "new" || signal.status === "reviewed").length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 bg-white border border-slate-200 shadow-sm p-1 rounded-lg mb-6">
          <TabsTrigger value="signals">Hidden Signals</TabsTrigger>
          <TabsTrigger value="drafts">Outreach Drafts</TabsTrigger>
        </TabsList>

        <TabsContent value="signals" className="m-0 outline-none">
          <div className="grid gap-4 lg:grid-cols-2">
            {signals.length > 0 ? (
              signals.map((signal) => (
                <Card key={signal.id} className="border-slate-200 shadow-sm">
                  <CardContent className="p-5 space-y-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <Badge className={statusTone[signal.status] || statusTone.reviewed}>{statusLabel(signal.status)}</Badge>
                          <Badge className={severityTone[signal.severity] || severityTone.low}>{signal.severity}</Badge>
                        </div>
                        <h3 className="text-lg font-semibold text-slate-900">{signal.title}</h3>
                        <p className="text-sm text-slate-500 mt-1">{signal.propertyAddress}</p>
                      </div>
                      <p className="text-xs text-slate-400 whitespace-nowrap">{formatRelative(signal.detectedAt)}</p>
                    </div>

                    <p className="text-sm text-slate-600 leading-6">{signal.description}</p>

                    <div className="flex flex-wrap gap-2">
                      {(signal.tags || []).slice(0, 6).map((tag) => (
                        <Badge key={tag} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button
                        className="gap-2"
                        onClick={() => handlePromote(signal)}
                        disabled={workingId === signal.id || signal.status === "promoted"}
                      >
                        {signal.status === "promoted" ? <CheckCircle2 className="h-4 w-4" /> : <WandSparkles className="h-4 w-4" />}
                        {signal.status === "promoted" ? "Promoted" : workingId === signal.id ? "Promoting..." : "Promote to Opportunity"}
                      </Button>
                      <Button
                        variant="outline"
                        className="gap-2"
                        onClick={() => createDraftFromSignal(signal, "sms")}
                        disabled={workingId === signal.id || signal.status === "promoted"}
                      >
                        <MessageSquareText className="h-4 w-4" /> Draft SMS
                      </Button>
                      <Button
                        variant="outline"
                        className="gap-2"
                        onClick={() => createDraftFromSignal(signal, "email")}
                        disabled={workingId === signal.id || signal.status === "promoted"}
                      >
                        <Mail className="h-4 w-4" /> Draft Email
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card className="border-dashed shadow-none lg:col-span-2">
                <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4">
                    <BellRing className="w-8 h-8 text-slate-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">No signals yet</h3>
                  <p className="text-slate-500 max-w-sm">
                    Civic data and drive-for-dollars insights will appear here once the app detects distressed properties.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="drafts" className="m-0 outline-none">
          <div className="grid gap-4">
            {drafts.length > 0 ? (
              drafts.map((draft) => (
                <Card key={draft.id} className="border-slate-200 shadow-sm">
                  <CardContent className="p-5 space-y-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <Badge className={statusTone[draft.status] || statusTone.draft}>{statusLabel(draft.status)}</Badge>
                          <Badge variant="outline">{channelLabel(draft.channel)}</Badge>
                        </div>
                        <h3 className="text-lg font-semibold text-slate-900">{draft.subject}</h3>
                        <p className="text-sm text-slate-500 mt-1">{draft.propertyAddress}</p>
                      </div>
                      <p className="text-xs text-slate-400 whitespace-nowrap">Updated {formatRelative(draft.updatedAt)}</p>
                    </div>

                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 whitespace-pre-wrap leading-6">
                      {draft.message}
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {(draft.tags || []).slice(0, 6).map((tag) => (
                        <Badge key={tag} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button variant="outline" className="gap-2" onClick={() => handleCopyDraft(draft)}>
                        <Copy className="h-4 w-4" /> Copy
                      </Button>
                      <Button className="gap-2" onClick={() => handleMarkSent(draft)} disabled={workingId === draft.id || draft.status === "sent"}>
                        <ExternalLink className="h-4 w-4" /> {draft.status === "sent" ? "Sent" : workingId === draft.id ? "Saving..." : "Mark Sent"}
                      </Button>
                      <Button variant="outline" className="gap-2" onClick={() => handleArchive(draft)} disabled={workingId === draft.id || draft.status === "archived"}>
                        <Archive className="h-4 w-4" /> Archive
                      </Button>
                      <Button variant="ghost" className="gap-2 text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={() => handleDelete(draft)} disabled={workingId === draft.id}>
                        <Trash2 className="h-4 w-4" /> Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card className="border-dashed shadow-none">
                <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4">
                    <MessageSquareText className="w-8 h-8 text-slate-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">No outreach drafts yet</h3>
                  <p className="text-slate-500 max-w-sm">
                    Create drafts from a hidden signal to keep your follow-up path saved and ready to send.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
